import moment from 'moment-timezone';
import isExists from './isExists';

/**
 * Single source of truth for the global (Topnav) date filter.
 *
 * The picked range lives in two places:
 *  - localStorage (`start_date`, `end_date`, `date_chosen_label`, `setdate`), which
 *    every list/stats screen reads synchronously on mount via getSelectedDate().
 *  - the logged-in user's `usersettings.date_filter` on the server, so the range
 *    picked in one browser is the range the app opens with in every other browser
 *    (localStorage alone is per-browser, which is why it used to fall back to the
 *    default elsewhere).
 *
 * Preset picks ("Last 30 Days", "This Month", ...) are stored as their label and the
 * dates are RECOMPUTED on every read, so a saved preset keeps meaning the same thing
 * on a later day instead of freezing to the window that was current when it was
 * picked. A custom range is stored as absolute dates and kept until it is changed.
 */

export const DEFAULT_RANGE_LABEL = 'Last 30 Days';

/**
 * Presets offered by the picker. Keys are the labels shown in the dropdown and
 * stored verbatim as `date_chosen_label`; key order is the order they are listed in.
 */
export function getPresetRanges() {
    return {
        'Last 30 Days': [
            moment().subtract(29, 'days').toDate(),
            moment().toDate()
        ],
        'Last 7 Days': [
            moment().subtract(6, 'days').toDate(),
            moment().toDate()
        ],
        'Last Month': [
            moment().subtract(1, 'months').startOf('month').toDate(),
            moment().subtract(1, 'months').endOf('month').toDate()
        ],
        'This Month': [
            moment().startOf('month').toDate(),
            moment().toDate()
        ],
        'All Time': [
            moment('2017-01-01').toDate(),
            moment().toDate()
        ],
        'Today': [
            moment().toDate(),
            moment().toDate()
        ],
        'Yesterday': [
            moment().subtract(1, 'days').toDate(),
            moment().subtract(1, 'days').toDate()
        ]
    };
}

/**
 * Dates for a preset label, or null when the label is a custom range.
 */
export function resolvePresetRange(label) {
    var ranges = getPresetRanges();
    if (!isExists(label) || !ranges[label]) {
        return null;
    }
    return { start_date: ranges[label][0], end_date: ranges[label][1] };
}

/**
 * Persist the range in localStorage the way a manual pick does.
 */
export function storeDateRange(label, start_date, end_date) {
    localStorage.setItem('start_date', start_date);
    localStorage.setItem('end_date', end_date);
    localStorage.setItem('setdate', moment().toDate());
    localStorage.setItem('date_chosen_label', label);
    return { start_date, end_date };
}

/**
 * The range the app should currently be filtered by, normalized back into
 * localStorage so getSelectedDate() consumers see the same window.
 */
export function readStoredDateRange() {
    var label = localStorage.getItem('date_chosen_label');
    var preset = resolvePresetRange(label);

    if (preset) {
        return storeDateRange(label, preset.start_date, preset.end_date);
    }

    // Custom range — keep exactly the dates that were picked.
    var stored_start = localStorage.getItem('start_date');
    var stored_end = localStorage.getItem('end_date');
    if (isExists(label) && isExists(stored_start) && isExists(stored_end)) {
        return storeDateRange(label, moment(stored_start).toDate(), moment(stored_end).toDate());
    }

    var fallback = resolvePresetRange(DEFAULT_RANGE_LABEL);
    return storeDateRange(DEFAULT_RANGE_LABEL, fallback.start_date, fallback.end_date);
}

/**
 * Seed localStorage from the account-level preference returned by the server.
 * Called before the app renders (see index.js) because every screen reads the
 * range synchronously on mount — hydrating later would leave the first fetch on
 * the default range.
 *
 * @param {*} saved { label, start_date, end_date } as stored server side
 * @returns the applied range, or null when there is nothing usable to apply
 */
export function hydrateDateRange(saved) {
    if (!saved || !isExists(saved.label)) {
        return null;
    }

    var preset = resolvePresetRange(saved.label);
    if (preset) {
        return storeDateRange(saved.label, preset.start_date, preset.end_date);
    }

    var start_date = moment(saved.start_date, 'YYYY-MM-DD', true);
    var end_date = moment(saved.end_date, 'YYYY-MM-DD', true);
    if (!start_date.isValid() || !end_date.isValid()) {
        return null;
    }
    return storeDateRange(saved.label, start_date.toDate(), end_date.toDate());
}

/**
 * Shape the range for the save endpoint (dates normalized to YYYY-MM-DD).
 */
export function dateRangePayload(label, start_date, end_date) {
    return {
        label: label,
        start_date: moment(start_date).format('YYYY-MM-DD'),
        end_date: moment(end_date).format('YYYY-MM-DD')
    };
}
